import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import settingsService from '../../../../services/settings.service';
import { showError } from '../../../../utils/ecxSwal';
import ClinicSettingsSectionHeader from '../clinicsettings/subparts/ClinicSettingsSectionHeader';
import ClinicSettingsListSkeleton from '../clinicsettings/subparts/ClinicSettingsListSkeleton';
import { ClinicInfoPanel } from '../clinicsettings/info/clinicInfoParts';
import {
    FeatureCategoryPanels,
    FeatureOverviewStats,
    FeatureSupportNote,
    buildFeatureCategories,
} from './featuresSettingsShared';

const FeatureCard = () => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [clinicFeatures, setClinicFeatures] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    const fetchClinicFeatures = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        setIsLoading(true);
        setLoadFailed(false);

        try {
            const res = await settingsService.getClinicFeatures(clinicId);
            setClinicFeatures(res.data || {});
        } catch (error) {
            console.error('Failed to fetch clinic features:', error);
            setLoadFailed(true);
            showError({
                title: 'Load Failed',
                text: 'Unable to load clinic features. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        fetchClinicFeatures();
    }, [fetchClinicFeatures]);

    const featureCategories = useMemo(
        () => buildFeatureCategories(clinicFeatures),
        [clinicFeatures]
    );

    const allFeatures = useMemo(
        () => featureCategories.flatMap((category) => category.features),
        [featureCategories]
    );

    const enabledCount = allFeatures.filter((feature) => feature.enabled).length;
    const disabledCount = allFeatures.length - enabledCount;

    if (isLoading) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="Feature Overview"
                    icon="bi-stars"
                    subtitle="Services and capabilities enabled for your clinic"
                />
                <FeatureOverviewStats totalCount={0} enabledCount={0} disabledCount={0} />
                <FeatureSupportNote />
                {buildFeatureCategories().map((category) => (
                    <ClinicInfoPanel key={category.title} title={category.title} icon={category.icon}>
                        <ClinicSettingsListSkeleton rows={category.features.length} />
                    </ClinicInfoPanel>
                ))}
            </>
        );
    }

    if (loadFailed) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="Feature Overview"
                    icon="bi-stars"
                    subtitle="Services and capabilities enabled for your clinic"
                />
                <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3">
                    <span>
                        <i className="bi bi-exclamation-triangle me-2" />
                        Failed to load clinic features.
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger rounded-pill"
                        onClick={fetchClinicFeatures}
                    >
                        <i className="bi bi-arrow-clockwise me-1" />
                        Retry
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Feature Overview"
                icon="bi-stars"
                subtitle="Services and capabilities enabled for your clinic"
                actions={
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="ecx-page-chip">
                            <i className="bi bi-grid-3x3-gap" />
                            {allFeatures.length} features
                        </span>
                        <span className="ecx-page-chip">
                            <i className="bi bi-check-circle" />
                            {enabledCount} enabled
                        </span>
                    </div>
                }
            />

            <FeatureOverviewStats
                totalCount={allFeatures.length}
                enabledCount={enabledCount}
                disabledCount={disabledCount}
            />

            <FeatureSupportNote />

            <FeatureCategoryPanels categories={featureCategories} />
        </>
    );
};

export default FeatureCard;
